import React, { useContext, useEffect } from 'react';
import { css } from '@emotion/css';
import { v4 } from 'uuid';
import { SearchTreeContext } from './SearchTree';
import { highLightHeavyClass, highLightNormalClass } from './index.style';
import { TreeNodeContext } from '~/components/Tree/Tree';
import useThemeProps from '~/hooks/useThemeProps';
import { ITheme } from '~/styles/interface';

interface ITreeLabelProps {
  label: string;
  _id: string;
}

interface IText {
  children: string;
  k: string;
}

const BaseTreeLabel: React.FC<ITreeLabelProps> = React.memo(({ label: _label, _id }) => {
  const theme = useThemeProps();
  const searchTreeContext = useContext(SearchTreeContext);
  const treeNodeContext = useContext(TreeNodeContext);
  const { nodeKey, isClone } = treeNodeContext;
  const rawLabel = React.useRef(_label);
  rawLabel.current = _label;

  if (_id === undefined) {
    throw Error(`an error occurred when TreeLabel init. \nlabel: ${_label}`);
  }

  const unionKey = searchTreeContext.createKey(nodeKey, _id);
  const [label, setLabel] = React.useState<React.ReactNode>(_label);

  const createChildren = (withMatch = true) => {
    const { matchText } = searchTreeContext;
    const text = rawLabel.current;

    if (!matchText || !withMatch) {
      setLabel(text);
    } else {
      const result = searchTreeContext.matched[unionKey];
      setLabel(result.children.map(({ rcNode }) => rcNode));
    }
  };

  useEffect(() => {
    if (isClone) {
      createChildren(Boolean(searchTreeContext.matched[unionKey]));
      return;
    }

    searchTreeContext.rigisterTextNode(unionKey, {
      label: _label,
      updateNode: createChildren,
    });
    // 展开后会挂载相邻的兄弟节点
    createChildren(Boolean(searchTreeContext.matched[unionKey]));

    return () => {
      searchTreeContext.removeTextNode(unionKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unionKey, _label]);

  const style = React.useMemo(() => treeLableStyle(theme), [theme]);

  return <span className={style}>{label}</span>;
});

class TreeLabel extends React.Component<{ label: string; children?: null }> {
  static displayName = 'TreeLabel';
  static __prism_rc_name = 'TreeLabel';
  static defaultProps: Partial<{ _id: string }> = {
    get _id() {
      const id = v4();
      return id;
    },
  };

  shouldComponentUpdate(nextProps: any) {
    if (nextProps.label === this.props.label) {
      return false;
    }

    return true;
  }

  render() {
    const { props } = this as any;
    return <BaseTreeLabel label={props.label} _id={props._id} />;
  }
}

const Text: React.FC<IText> = ({ children, k }) => {
  const searchTreeContext = useContext(SearchTreeContext);
  const { isClone } = useContext(TreeNodeContext);
  const [isCurrent, setIsCurrent] = React.useState(false);
  const { matched, currentKey, parseKey, createKey } = searchTreeContext;
  const [nodeKey, treeLabelIndex, index] = parseKey(k);
  const self = matched[createKey(nodeKey, treeLabelIndex)].children[Number(index)];

  // 保证节点更新绑上最新的highlight
  useEffect(() => {
    if (isClone) {
      setIsCurrent(currentKey === k);
      return;
    }

    self.highLight = (isSelf: boolean) => {
      setIsCurrent(isSelf);
    };

    setIsCurrent(currentKey === k);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [k, children, self]);

  return (
    <span className={isCurrent ? highLightHeavyClass() : highLightNormalClass()}>{children}</span>
  );
};

Text.displayName = 'TreeLabelText';

const treeLableStyle = (theme: ITheme) => css`
  display: inline !important;

  .${highLightNormalClass()} {
    color: ${theme.isSyetemThemeDark ? theme.background : theme.primary};
    background-color: ${theme.pending};
  }

  .${highLightHeavyClass()} {
    color: ${theme.isSyetemThemeDark ? theme.background : theme.primary};
    background-color: ${theme.warning};
  }
`;

export default TreeLabel;
export { Text };
export type { ITreeLabelProps, IText };
